import React, { ReactNode, useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Pets as PetsIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Favorite as FavoriteIcon,
  Park as ParkIcon,
  AutoAwesome as AutoAwesomeIcon,
  Summarize as SummarizeIcon,
  LocationCity as LocationCityIcon,
  Language as LanguageIcon,
  AdminPanelSettings as AdminIcon,
  Code as CodeIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon,
  TableChart as TableChartIcon,
  MedicalServices as MedicalIcon,
  Public as PublicIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import ChangePasswordDialog from './ChangePasswordDialog';
import api from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Props {
  children: ReactNode;
}

// All nav entries stay visible in one row; each category has its own colour
// (tint on the bar, solid colour when the page is active) so the groups are
// recognisable at a glance without hiding anything behind a dropdown.
const navGroups = [
  {
    key: 'shelter',
    tint: '#ffcc80',   // amber
    solid: '#e65100',
    items: [
      { path: '/cats-list', labelKey: 'menu.catsShelter', icon: <PetsIcon fontSize="small" /> },
      { path: '/adopted-list', labelKey: 'menu.adopted', icon: <FavoriteIcon fontSize="small" /> },
      { path: '/freed-list', labelKey: 'menu.freed', icon: <ParkIcon fontSize="small" /> },
      { path: '/angels-list', labelKey: 'menu.angels', icon: <AutoAwesomeIcon fontSize="small" /> },
    ],
  },
  {
    key: 'external',
    tint: '#a5d6a7',   // green
    solid: '#2e7d32',
    items: [
      { path: '/external-list', labelKey: 'menu.externalCats', icon: <PublicIcon fontSize="small" /> },
    ],
  },
  {
    key: 'management',
    tint: '#80deea',   // cyan
    solid: '#00838f',
    items: [
      { path: '/expenses', labelKey: 'menu.expenses', icon: <MedicalIcon fontSize="small" /> },
      { path: '/report', labelKey: 'menu.reporting', icon: <SummarizeIcon fontSize="small" /> },
      { path: '/statistics', labelKey: 'menu.statistics', icon: <BarChartIcon fontSize="small" /> },
      { path: '/municipalities', labelKey: 'menu.municipalities', icon: <LocationCityIcon fontSize="small" /> },
    ],
  },
];

const adminItems = [
  { path: '/data', labelKey: 'menu.dataManagement', icon: <TableChartIcon fontSize="small" /> },
  { path: '/query', labelKey: 'menu.queryInterface', icon: <CodeIcon fontSize="small" /> },
  { path: '/schema', labelKey: 'menu.schemaBrowser', icon: <StorageIcon fontSize="small" /> },
  { path: '/stats', labelKey: 'menu.databaseStats', icon: <AssessmentIcon fontSize="small" /> },
  { path: '/users', labelKey: 'menu.userManagement', icon: <PeopleIcon fontSize="small" /> },
];

export default function Layout({ children }: Props) {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<null | HTMLElement>(null);
  const [versionInfo, setVersionInfo] = useState<{ app_version: string; migrations: any[] } | null>(null);

  useEffect(() => {
    api.get(`${API_URL}/api/version`).then(r => setVersionInfo(r.data)).catch(() => {});
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { t, i18n } = useTranslation();

  const isSuperadmin = user?.role === 'superadmin';

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => setLanguageMenuAnchor(event.currentTarget);
  const handleLanguageMenuClose = () => setLanguageMenuAnchor(null);
  const changeLanguage = (lang: string) => { i18n.changeLanguage(lang); handleLanguageMenuClose(); };

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAdminMenuAnchor(event.currentTarget);
  const handleAdminMenuClose = () => setAdminMenuAnchor(null);

  const isPathActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');
  const isAdminPath = adminItems.some((item) => location.pathname === item.path);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 2px 12px rgba(102,126,234,0.3)',
        }}
      >
        {/* Top row: logo + user actions */}
        <Toolbar sx={{ minHeight: '52px !important', px: 2 }}>
          <Box sx={{ mr: 2 }}>
            <Typography component="span" sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: 0.5, color: 'white' }}>
              MeiMei
            </Typography>
            <Typography component="span" sx={{ fontWeight: 400, fontSize: '0.95rem', letterSpacing: 0.3, color: 'rgba(255,255,255,0.82)', ml: 0.8 }}>
              Gli Amici di Poldo
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" sx={{ opacity: 0.85, mr: 1, fontSize: '0.8rem' }}>
            {user?.username}
          </Typography>
          <Tooltip title={t('language.language')}>
            <IconButton color="inherit" onClick={handleLanguageMenuOpen} size="small">
              <LanguageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={languageMenuAnchor}
            open={Boolean(languageMenuAnchor)}
            onClose={handleLanguageMenuClose}
            PaperProps={{ sx: { borderRadius: 2, minWidth: 120 } }}
          >
            <MenuItem onClick={() => changeLanguage('en')} selected={i18n.language === 'en'}>{t('language.english')}</MenuItem>
            <MenuItem onClick={() => changeLanguage('it')} selected={i18n.language === 'it'}>{t('language.italian')}</MenuItem>
          </Menu>
          <Tooltip title={t('auth.changePassword')}>
            <IconButton color="inherit" onClick={() => setChangePasswordOpen(true)} size="small">
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('auth.logout')}>
            <IconButton color="inherit" onClick={logout} size="small">
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>

        {/* Navigation row: all entries visible, colour-coded by category */}
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', px: 1, py: 0.5, background: 'rgba(0,0,0,0.12)' }}>
          {navGroups.map((group, gi) => (
            <React.Fragment key={group.key}>
              {gi > 0 && (
                <Box sx={{ width: '1px', alignSelf: 'stretch', my: 0.6, mx: 0.9, background: 'rgba(255,255,255,0.25)' }} />
              )}
              {group.items.map((item) => {
                const active = isPathActive(item.path);
                return (
                  <Button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    startIcon={item.icon}
                    sx={{
                      minHeight: 38,
                      px: 1.6,
                      mx: 0.25,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontSize: '0.88rem',
                      whiteSpace: 'nowrap',
                      fontWeight: active ? 700 : 500,
                      color: active ? group.solid : group.tint,
                      background: active ? 'white' : 'transparent',
                      boxShadow: active ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
                      transition: 'background 0.15s, color 0.15s',
                      '&:hover': {
                        background: active ? 'white' : 'rgba(255,255,255,0.14)',
                        color: active ? group.solid : 'white',
                      },
                    }}
                  >
                    {t(item.labelKey)}
                  </Button>
                );
              })}
            </React.Fragment>
          ))}
          <Box sx={{ flexGrow: 1 }} />
          {isSuperadmin && (
            <>
              <Tooltip title="Admin">
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={handleAdminMenuOpen}
                  sx={{ opacity: isAdminPath ? 1 : 0.65, mr: 0.5 }}
                >
                  <AdminIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={adminMenuAnchor}
                open={Boolean(adminMenuAnchor)}
                onClose={handleAdminMenuClose}
                PaperProps={{ sx: { borderRadius: 2 } }}
              >
                {adminItems.map((item) => (
                  <MenuItem
                    key={item.path}
                    onClick={() => { navigate(item.path); handleAdminMenuClose(); }}
                    selected={location.pathname === item.path}
                    sx={{ gap: 1.5, fontSize: '0.875rem' }}
                  >
                    {item.icon}
                    {t(item.labelKey)}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </AppBar>

      <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: '100px',
          px: 2,
          pb: 5,
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
        }}
      >
        {children}
      </Box>

      {/* Version footer */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          py: 0.4,
          px: 2,
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(6px)',
          borderTop: '1px solid rgba(102,126,234,0.12)',
          display: 'flex',
          justifyContent: 'flex-end',
          zIndex: 1000,
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem', letterSpacing: 0.3 }}>
          {versionInfo
            ? `v${versionInfo.app_version} · ${versionInfo.migrations.length} migration${versionInfo.migrations.length !== 1 ? 's' : ''} applied`
            : '…'}
        </Typography>
      </Box>
    </Box>
  );
}
